import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FileText, Download, Loader2, Mail, ArrowLeft, HandCoins, TrendingUp, CreditCard, UserPlus, Building2, Phone, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, PUBLIC_API, formatAmount, apiClient } from "../lib/api";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import IdentityVerification from "../components/IdentityVerification";
import AddressAutocomplete from "../components/shared/AddressAutocomplete";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../i18n/LanguageSwitcher";

// Statuts backend (pending, partial…) → clés de traduction. Les VALEURS de
// statut restent celles de l'API ; seul le libellé affiché est traduit.
const STATUS_LABEL_KEYS = {
  pending: "portal.status_pending",
  partial: "portal.status_partial",
  paid: "portal.status_paid",
  cancelled: "portal.status_cancelled",
};
const STATUS_STYLES = {
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  partial:   "bg-blue-50 text-blue-700 border-blue-200",
  paid:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-stone-100 text-stone-500 border-stone-200",
};

// ─── Request page (enter email) ────────────────────────────────────────────
// Avec le nouveau flow de vérification d'identité unifié (lien dans le mail
// + page "Oui c'est bien moi"), on redirige directement vers l'espace donateur
// après confirmation — donc plus besoin de l'ancien écran "Email envoyé".
function RequestPortalPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [tab, setTab] = useState("access"); // "access" | "register"

  // Shared error state (lookup-orgs failures, etc.)
  const [emailError, setEmailError] = useState(null);

  // "Access" tab state
  const [accessMode, setAccessMode] = useState("email"); // "email" | "phone"
  const [email, setEmail] = useState("");
  const [accessPhone, setAccessPhone] = useState("");
  const [notFound, setNotFound] = useState(false); // true when account not found
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Identity-verification flow (uniform with Kiddoush / dons publics / campagnes)
  // accessStep:
  //   "input"  → user types email/phone (default)
  //   "pick"   → multi-org chooser shown
  //   "verify" → IdentityVerification widget shown
  //   "done"   → exchange in progress / redirect imminent
  const [accessStep, setAccessStep] = useState("input");
  const [accessOrgs, setAccessOrgs] = useState([]); // [{id, name}]
  const [accessOrgId, setAccessOrgId] = useState("");
  const [accessFirstName, setAccessFirstName] = useState(""); // for IdentityVerification greeting
  // Donateur reconnu (meilleur effort) : ouvre le choix du canal de
  // vérification (lien email / code SMS, y compris vers le contact enregistré).
  const [accessRecognized, setAccessRecognized] = useState(null);

  // "Register" tab state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [loadingReg, setLoadingReg] = useState(false);
  const [regError, setRegError] = useState("");

  // Register flow steps:
  //   "form"   → fill the form (default)
  //   "verify" → IdentityVerification widget shown after submit
  //   "done"   → register-verified in progress / redirect imminent
  const [regStep, setRegStep] = useState("form");

  // Load orgs when "register" tab is first opened
  useEffect(() => {
    if (tab === "register" && orgs.length === 0 && !orgsLoading) {
      setOrgsLoading(true);
      axios.get(`${PUBLIC_API}/organizations/public/directory`)
        .then(r => setOrgs(r.data?.organizations || []))
        .catch(() => toast.error(t("portal.err_load_orgs")))
        .finally(() => setOrgsLoading(false));
    }
  }, [tab]);

  const reset = () => {
    setEmailError(null); setNotFound(false);
    setEmail(""); setAccessPhone(""); setRegEmail(""); setFirstName(""); setLastName("");
    setPhone(""); setOrgId(""); setRegError("");
    setAccessStep("input"); setAccessOrgs([]); setAccessOrgId(""); setAccessFirstName("");
    setAccessRecognized(null);
    setRegStep("form");
  };

  // ── "Access" — Étape 1 : lookup pour savoir si le donateur existe et
  //    récupérer la liste de ses synagogues. On bascule ensuite direct
  //    sur la vérification d'identité (plus de chooser : si plusieurs
  //    synagogues, le donateur les verra toutes en onglets dans son
  //    espace après confirmation).
  const submitAccess = async (e) => {
    e.preventDefault();
    const hasEmail = accessMode === "email" && email.trim();
    const hasPhone = accessMode === "phone" && accessPhone.trim();
    if (!hasEmail && !hasPhone) return;
    setLoadingAccess(true);
    setEmailError(null);
    setNotFound(false);
    try {
      const payload = accessMode === "email"
        ? { email: email.trim() }
        : { phone: accessPhone.trim() };
      const res = await apiClient.post(`/portal/lookup-orgs`, payload, { timeout: 30000 });
      if (!res.data.found || (res.data.orgs || []).length === 0) {
        setNotFound(true);
        return;
      }
      setAccessFirstName(res.data.first_name || "");
      const orgList = res.data.orgs || [];
      setAccessOrgs(orgList);
      // On utilise la première synagogue pour le branding du widget de
      // vérif d'identité (logo, couleurs). Le jeton portail final couvrira
      // toutes les synagogues du donateur (multi-org).
      setAccessOrgId(orgList[0].id);
      // Reconnaissance (meilleur effort) — SANS attendre : le bouton
      // « Suivant » ne doit pas ralentir ; le choix du canal s'enrichit dès
      // que la réponse arrive, et sans elle tout marche comme avant.
      axios.get(`${PUBLIC_API}/campaigns/public/recognize-donor`, {
        params: accessMode === "email"
          ? { org_id: orgList[0].id, email: email.trim() }
          : { org_id: orgList[0].id, phone: accessPhone.trim() },
      })
        .then((rec) => setAccessRecognized(rec.data?.found ? rec.data : null))
        .catch(() => setAccessRecognized(null));
      setAccessStep("verify");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (!err?.response) toast.error(t("portal.err_server_unreachable"));
      else toast.error(typeof detail === "string" ? detail : t("portal.err_generic_status", { status: status || "" }));
    } finally { setLoadingAccess(false); }
  };

  // ── "Access" — Étape 3 : appelée par IdentityVerification quand le
  //    donateur a confirmé "Oui c'est bien moi". On échange le jeton de
  //    vérification contre un jeton portail MULTI-SYNAGOGUE puis on redirige.
  const handleAccessVerified = async (verificationToken) => {
    try {
      setAccessStep("done");
      // Pas d'organization_id → on demande un token multi-synagogue,
      // l'espace donateur affichera des onglets si plusieurs.
      const res = await apiClient.post(`/portal/exchange-verification`, {
        verification_token: verificationToken,
      }, { timeout: 30000 });
      const portalToken = res.data?.portal_token;
      if (portalToken) {
        navigate(`/mon-espace/${portalToken}`);
      } else {
        toast.error(t("portal.err_unexpected"));
        setAccessStep("verify");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.err_exchange"));
      setAccessStep("verify");
    }
  };

  // ── "Register" — Étape 1 : valide le formulaire puis bascule sur la
  //    vérification d'identité. On crée le compte SEULEMENT après vérif.
  const submitRegister = (e) => {
    e.preventDefault();
    setRegError("");
    if (!firstName || !lastName) { setRegError(t("portal.err_name_required")); return; }
    if (!regEmail) { setRegError(t("portal.err_email_required")); return; }
    if (!orgId) { setRegError(t("portal.err_org_required")); return; }
    setRegStep("verify");
  };

  // ── "Register" — Étape 3 : après confirmation d'identité, on crée
  //    le donateur et on récupère le jeton portail, puis on redirige.
  const handleRegisterVerified = async (verificationToken) => {
    try {
      setRegStep("done");
      const res = await apiClient.post(`/portal/register-verified`, {
        verification_token: verificationToken,
        organization_id: orgId,
        first_name: firstName,
        last_name: lastName,
        phone: phone || undefined,
      }, { timeout: 30000 });
      const portalToken = res.data?.portal_token;
      if (portalToken) {
        navigate(`/mon-espace/${portalToken}`);
      } else {
        toast.error(t("portal.err_unexpected"));
        setRegStep("verify");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.err_register"));
      setRegStep("verify");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "#F4F3EF" }}>
      <div className="bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-lg">
        {/* Sélecteur de langue (discret, en haut de la carte) */}
        <div className="flex justify-end -mt-3 -mr-2">
          <LanguageSwitcher />
        </div>
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <TeroumaLogo textColor="#0D1B33" />
        </div>

        {/* Error state */}
        {emailError ? (
          <>
            <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-7 w-7 text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-stone-900 text-center mb-2">{t("portal.send_problem_title")}</h2>
            <p className="text-stone-500 text-sm text-center mb-4">{emailError}</p>
            <button onClick={() => setEmailError(null)} className="w-full text-sm text-stone-600 underline">{t("common.retry")}</button>
          </>
        ) : (
          <>
            {/* Title */}
            <h1 className="text-2xl font-semibold text-stone-900 text-center mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("portal.title")}
            </h1>
            <p className="text-stone-500 text-sm text-center mb-6">
              {t("portal.subtitle")}
            </p>

            {/* Tabs */}
            <div className="flex bg-stone-100 rounded-2xl p-1 mb-6">
              <button
                onClick={() => setTab("access")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-xl transition-all ${tab === "access" ? "bg-white shadow-sm text-stone-900" : "text-stone-500 hover:text-stone-700"}`}
              >
                <Mail className="h-3.5 w-3.5" />
                {t("portal.tab_access")}
              </button>
              <button
                onClick={() => setTab("register")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-xl transition-all ${tab === "register" ? "bg-white shadow-sm text-stone-900" : "text-stone-500 hover:text-stone-700"}`}
              >
                <UserPlus className="h-3.5 w-3.5" />
                {t("portal.tab_register")}
              </button>
            </div>

            {/* ── Tab: Access ── */}
            {tab === "access" && (
              <div className="space-y-4">
                {/* Étape 1 : saisie email/téléphone */}
                {accessStep === "input" && (
                  <>
                    {/* Mode toggle: Email / Téléphone */}
                    <div className="flex gap-1 p-1 bg-stone-100 rounded-xl">
                      <button type="button" onClick={() => { setAccessMode("email"); setNotFound(false); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all ${accessMode === "email" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>
                        <Mail className="h-3.5 w-3.5" /> {t("common.email")}
                      </button>
                      <button type="button" onClick={() => { setAccessMode("phone"); setNotFound(false); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all ${accessMode === "phone" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>
                        <Phone className="h-3.5 w-3.5" /> {t("common.phone")}
                      </button>
                    </div>

                    <form onSubmit={submitAccess} className="space-y-3">
                      {accessMode === "email" ? (
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                          <input type="email" value={email} onChange={e => { setEmail(e.target.value); setNotFound(false); }}
                            placeholder={t("portal.email_placeholder")} required
                            className="w-full py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            style={{ paddingLeft: "2.5rem" }} />
                        </div>
                      ) : (
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                          <input type="tel" value={accessPhone} onChange={e => { setAccessPhone(e.target.value); setNotFound(false); }}
                            placeholder="+33 6 12 34 56 78" required
                            className="w-full py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            style={{ paddingLeft: "2.5rem" }} />
                        </div>
                      )}

                      {/* Not found message */}
                      {notFound && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                          <p className="text-sm font-medium text-amber-800 mb-1">
                            {accessMode === "email" ? t("portal.not_found_email") : t("portal.not_found_phone")}
                          </p>
                          <p className="text-xs text-amber-600 mb-2">
                            {t("portal.not_found_hint")}
                          </p>
                          <button type="button" onClick={() => { setTab("register"); setNotFound(false); }}
                            className="text-xs font-semibold underline text-amber-700 hover:text-amber-900">
                            {t("portal.create_account_cta")}
                          </button>
                        </div>
                      )}

                      <button type="submit" disabled={loadingAccess || (accessMode === "email" ? !email : !accessPhone)}
                        className="w-full py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
                        style={{ background: "#0D1B33" }}>
                        {loadingAccess ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        {t("common.next")}
                      </button>
                      <p className="text-xs text-stone-400 text-center">{t("portal.two_step_note")}</p>
                    </form>
                  </>
                )}

                {/* Étape 2 : vérification d'identité (même flow que Kiddoush/Dons).
                    Si le donateur est dans plusieurs synagogues, il les verra toutes
                    en onglets dans son espace après confirmation — donc plus de
                    chooser avant. */}
                {accessStep === "verify" && (
                  <div className="space-y-3">
                    {accessOrgs.length > 1 && (
                      <div className="px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 leading-relaxed">
                        {t("portal.multi_org_prefix")} <strong>{t("portal.multi_org_count", { count: accessOrgs.length })}</strong>.
                        {" "}{t("portal.multi_org_suffix")}
                      </div>
                    )}
                    <p className="text-sm text-stone-700">
                      {t("portal.verify_access_text")}
                    </p>
                    <IdentityVerification
                      organizationId={accessOrgId}
                      email={accessMode === "email" ? email.trim() : ""}
                      phone={accessMode === "phone" ? accessPhone.trim() : ""}
                      firstName={accessFirstName}
                      recognized={accessRecognized}
                      onVerified={handleAccessVerified}
                      context="portal"
                    />
                    <button type="button" onClick={() => setAccessStep("input")}
                      className="w-full text-xs text-stone-400 underline hover:text-stone-600">
                      ← {t("common.back")}
                    </button>
                  </div>
                )}

                {accessStep === "done" && (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-sm text-stone-600">{t("portal.opening_space")}</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Tab: Register ── */}
            {tab === "register" && (
              <>
                {/* Étape 1 : formulaire */}
                {regStep === "form" && (
                  <form onSubmit={submitRegister} className="space-y-3">
                    {/* Name row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none" />
                        <input
                          value={firstName} onChange={e => setFirstName(e.target.value)}
                          placeholder={`${t("common.first_name")} *`}
                          className="w-full py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                          style={{ paddingLeft: "2.25rem" }}
                        />
                      </div>
                      <input
                        value={lastName} onChange={e => setLastName(e.target.value)}
                        placeholder={`${t("common.last_name")} *`}
                        className="w-full px-3 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                      />
                    </div>

                    {/* Email */}
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)}
                        placeholder={`${t("common.email")} *`}
                        className="w-full py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                        style={{ paddingLeft: "2.5rem" }}
                      />
                    </div>

                    {/* Phone (optional) */}
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                      <input
                        type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                        placeholder={`${t("common.phone")} (${t("common.optional")})`}
                        className="w-full py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                        style={{ paddingLeft: "2.5rem" }}
                      />
                    </div>

                    {/* Org selector */}
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                      {orgsLoading ? (
                        <div className="flex items-center gap-2 w-full py-3 px-10 rounded-xl border border-stone-200 text-sm text-stone-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("common.loading")}
                        </div>
                      ) : (
                        <select
                          value={orgId} onChange={e => setOrgId(e.target.value)}
                          className="w-full py-3 rounded-xl border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 appearance-none"
                          style={{ paddingLeft: "2.5rem", paddingRight: "1rem" }}
                        >
                          <option value="">{t("portal.select_org")}</option>
                          {orgs.map(o => (
                            <option key={o.id} value={o.id}>{o.name}{o.city ? ` — ${o.city}` : ""}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {regError && (
                      <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{regError}</p>
                    )}

                    <button type="submit" disabled={loadingReg}
                      className="w-full py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
                      style={{ background: "#0D1B33" }}>
                      {loadingReg ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                      {t("common.next")}
                    </button>

                    <p className="text-xs text-stone-400 text-center">
                      {t("portal.two_step_register_note")}
                    </p>
                  </form>
                )}

                {/* Étape 2 : vérification d'identité avant création */}
                {regStep === "verify" && (
                  <div className="space-y-3">
                    <p className="text-sm text-stone-700">
                      {t("portal.verify_register_text")}
                    </p>
                    <IdentityVerification
                      organizationId={orgId}
                      email={regEmail.trim()}
                      phone={(phone || "").trim()}
                      firstName={firstName}
                      onVerified={handleRegisterVerified}
                      context="portal-register"
                    />
                    <button type="button" onClick={() => setRegStep("form")}
                      className="w-full text-xs text-stone-400 underline hover:text-stone-600">
                      {t("portal.back_to_form")}
                    </button>
                  </div>
                )}

                {regStep === "done" && (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-sm text-stone-600">{t("portal.creating_space")}</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Donor dashboard (accessed via token) ─────────────────────────────────
function DonorProfileSection({ donor, token, onUpdate }) {
  const { t } = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const blank = () => ({
    first_name: donor?.first_name || "",
    last_name: donor?.last_name || "",
    civility: donor?.civility || "",
    company_name: donor?.company_name || "",
    siren: donor?.siren || "",
    phone: donor?.phone || "",
    address: donor?.address || "",
    postal_code: donor?.postal_code || "",
    city: donor?.city || "",
    country: donor?.country || "",
    // Adresse SOCIÉTÉ (siège) DISTINCTE de l'adresse perso. On NE pré-remplit PAS
    // avec l'adresse perso (sinon les deux deviendraient identiques par recopie) :
    // champ vide si la fiche n'a pas d'adresse société. Le Cerfa entreprise retombe
    // de toute façon sur l'adresse perso côté serveur si le siège est vide.
    company_address: donor?.company_address || "",
    company_postal_code: donor?.company_postal_code || "",
    company_city: donor?.company_city || "",
    company_country: donor?.company_country || "",
  });
  const [formData, setFormData] = useState(blank);
  // Société FACULTATIVE : le profil reste « personne ». On coche pour ajouter
  // une société. Le choix particulier/entreprise se fait ensuite AU PAIEMENT.
  const [hasCompany, setHasCompany] = useState(!!donor?.company_name);

  const upd = (k) => (e) => setFormData((d) => ({ ...d, [k]: e.target.value }));
  const inputCls =
    "w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-900 outline-none text-sm";
  const labelCls = "block text-sm font-medium text-stone-700 mb-1.5";

  // Infos manquantes (incitation à compléter).
  const missing = [];
  if (!donor?.phone) missing.push(t("portal.missing_phone"));
  if (!donor?.address) missing.push(t("portal.missing_address"));

  // Si une société est déclarée, ses champs (pour un Cerfa valable) doivent être complets.
  const companyInvalid =
    hasCompany &&
    (!formData.company_name.trim() ||
      !formData.company_address.trim() ||
      !formData.company_postal_code.trim() ||
      !formData.company_city.trim());

  const startEditing = () => {
    setFormData(blank());
    setHasCompany(!!donor?.company_name);
    setIsEditing(true);
  };

  // Raccourci : ouvrir l'édition directement avec la section société dépliée.
  const startEditingCompany = () => {
    setFormData(blank());
    setHasCompany(true);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        // Le profil reste « personne » ; la société est une info en plus. Le
        // choix particulier/entreprise se fait AU PAIEMENT (champ paid_as).
        donor_type: "particulier",
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        phone: formData.phone || null,
        address: formData.address || null,
        postal_code: formData.postal_code || null,
        city: formData.city || null,
        country: formData.country || null,
        // "" (et non null) pour EFFACER la société si on décoche (le backend
        // ignore les null mais applique les chaînes vides).
        civility: hasCompany ? (formData.civility || null) : "",
        company_name: hasCompany ? (formData.company_name || null) : "",
        siren: hasCompany ? (formData.siren || null) : "",
        // Adresse du SIÈGE → champs société dédiés (distincts de l'adresse perso
        // ci-dessus). "" pour effacer si on décoche la société.
        company_address: hasCompany ? (formData.company_address || null) : "",
        company_postal_code: hasCompany ? (formData.company_postal_code || null) : "",
        company_city: hasCompany ? (formData.company_city || null) : "",
        company_country: hasCompany ? (formData.company_country || null) : "",
      };
      const response = await fetch(`${PUBLIC_API}/portal/${token}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(t("portal.profile_updated"));
        setIsEditing(false);
        onUpdate && onUpdate();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(
          typeof data?.detail === "string"
            ? data.detail
            : t("portal.err_update")
        );
      }
    } catch (e) {
      toast.error(t("portal.err_network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-stone-900">{t("portal.profile_title")}</h3>
        {!isEditing && (
          <button
            onClick={startEditing}
            className="text-sm px-3 py-1.5 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors"
          >
            {t("portal.edit")}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>{t("common.phone")}</label>
            <input type="tel" value={formData.phone} onChange={upd("phone")} placeholder="+33 6 12 34 56 78" className={inputCls} />
          </div>

          {/* Société — FACULTATIVE (la case adapte le formulaire). */}
          <label className="flex items-center gap-2.5 cursor-pointer rounded-xl border border-stone-200 p-3 bg-stone-50/40">
            <input
              type="checkbox"
              checked={hasCompany}
              onChange={(e) => setHasCompany(e.target.checked)}
              className="h-4 w-4 rounded border-stone-300 text-stone-900"
            />
            <span className="text-sm font-medium text-stone-800">{t("portal.has_company_label")}</span>
          </label>

          {hasCompany ? (
            <div className="space-y-3">
              <div>
                <label className={labelCls}>{t("common.company_name")} <span className="text-red-500">*</span></label>
                <input value={formData.company_name} onChange={upd("company_name")} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>SIREN <span className="text-stone-400 font-normal">({t("common.optional")})</span></label>
                  <input value={formData.siren} onChange={upd("siren")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("portal.rep_civility")}</label>
                  <select value={formData.civility} onChange={upd("civility")} className={inputCls}>
                    <option value="">—</option>
                    <option value="M.">{t("portal.civility_mr")}</option>
                    <option value="Mme">{t("portal.civility_mrs")}</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t("portal.rep_first_name")}</label>
                  <input value={formData.first_name} onChange={upd("first_name")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("portal.rep_last_name")}</label>
                  <input value={formData.last_name} onChange={upd("last_name")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("portal.company_address_label")} <span className="text-red-500">*</span></label>
                <AddressAutocomplete
                  value={formData.company_address}
                  onChange={(v) => setFormData((d) => ({ ...d, company_address: v }))}
                  onSelect={({ address, postalCode, city }) => setFormData((d) => ({ ...d, company_address: address, company_postal_code: postalCode, company_city: city }))}
                  placeholder={t("portal.address_placeholder")}
                  inputClassName={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t("common.postal_code")} <span className="text-red-500">*</span></label>
                  <input value={formData.company_postal_code} onChange={upd("company_postal_code")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("common.city")} <span className="text-red-500">*</span></label>
                  <input value={formData.company_city} onChange={upd("company_city")} className={inputCls} />
                </div>
              </div>
              <p className="text-[11px] text-stone-500">
                {t("portal.company_note_1")} <strong>{t("portal.company_note_strong")}</strong> {t("portal.company_note_2")}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t("common.first_name")}</label>
                  <input value={formData.first_name} onChange={upd("first_name")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("common.last_name")}</label>
                  <input value={formData.last_name} onChange={upd("last_name")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("portal.address_label")}</label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(v) => setFormData((d) => ({ ...d, address: v }))}
                  onSelect={({ address, postalCode, city }) => setFormData((d) => ({ ...d, address, postal_code: postalCode, city }))}
                  placeholder={t("portal.address_placeholder")}
                  inputClassName={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t("common.postal_code")}</label>
                  <input value={formData.postal_code} onChange={upd("postal_code")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("common.city")}</label>
                  <input value={formData.city} onChange={upd("city")} className={inputCls} />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || companyInvalid}
              className="flex-1 bg-stone-900 text-white py-2.5 rounded-lg hover:bg-stone-800 disabled:opacity-50 text-sm font-medium transition-colors"
            >
              {saving ? t("portal.saving") : t("portal.save")}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="flex-1 border border-stone-200 py-2.5 rounded-lg hover:bg-stone-50 text-sm font-medium transition-colors"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <p><strong>{t("portal.field_name")}</strong> {donor?.first_name} {donor?.last_name}</p>
          {donor?.phone && <p><strong>{t("portal.field_phone")}</strong> {donor.phone}</p>}
          {(donor?.address || donor?.city) && (
            <p><strong>{t("portal.field_address")}</strong> {[donor?.address, donor?.postal_code, donor?.city, donor?.country].filter(Boolean).join(", ")}</p>
          )}
          {donor?.company_name ? (
            <p className="pt-1">
              <strong>{t("portal.field_company")}</strong> {donor.company_name}
              {donor?.siren ? ` · SIREN ${donor.siren}` : ""}
            </p>
          ) : (
            <button
              type="button"
              onClick={startEditingCompany}
              data-testid="portal-add-company"
              className="mt-3 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-dashed border-stone-300 text-sm font-medium text-stone-700 hover:border-stone-900 hover:bg-stone-50 transition-colors"
            >
              <Building2 className="h-4 w-4" />
              {t("portal.add_company")}
            </button>
          )}
          {missing.length > 0 && (
            <p className="text-amber-600 mt-3">{t("portal.missing_info", { list: missing.join(", ") })}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DonorDashboard({ token }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  // Format des dates affichées selon la langue choisie (contenu inchangé).
  const dateLocale = lang === "fr" ? "fr-FR" : "en-GB";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState({});
  const [payChoice, setPayChoice] = useState(null); // pledgeId en cours de choix perso/société
  // Onglet actif pour le mode multi-synagogue. null = "Toutes".
  const [activeOrgId, setActiveOrgId] = useState(null);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/portal/${token}`)
      .then(r => setData(r.data))
      // true = message générique traduit AU RENDU (permet de changer de langue après coup)
      .catch(e => setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : true))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadCerfa = async (cerfa) => {
    try {
      const res = await fetch(`${PUBLIC_API}/portal/${token}/cerfa/${cerfa.id}.pdf`);
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${cerfa.number || cerfa.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("portal.err_download")); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#F4F3EF" }}>
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-lg">
        <p className="text-stone-900 font-semibold mb-2">{t("portal.link_expired")}</p>
        <p className="text-stone-500 text-sm mb-6">{typeof error === "string" ? error : t("portal.err_invalid_link")}</p>
        <Link to="/mon-espace" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium" style={{ background: "#0D1B33" }}>
          {t("portal.request_new_link")}
        </Link>
      </div>
    </div>
  );

  const { donor, total_donated } = data;
  // Liste des synagogues (mode multi-org). Si une seule, on n'affiche pas
  // d'onglets et le contenu reste plat comme avant.
  const orgsList = data.organizations || [];
  const isMultiOrg = orgsList.length > 1;

  // Filtrage selon l'onglet actif. activeOrgId === null = "Toutes".
  const activeOrg = activeOrgId
    ? orgsList.find(o => o.id === activeOrgId)
    : null;
  const pledges = activeOrg ? activeOrg.pledges : (data.pledges || []);
  const cerfas = activeOrg ? activeOrg.cerfas : (data.cerfas || []);
  const totalDisplayed = activeOrg ? activeOrg.total_donated : total_donated;
  // Le donateur affiché en haut de page : si on regarde UNE synagogue
  // précise, on prend son nom dans cette synagogue (souvent identique
  // mais peut différer si fiches désynchronisées) ; sinon, identité primaire.
  const displayDonor = activeOrg && activeOrg.donor && activeOrg.donor.first_name
    ? activeOrg.donor
    : donor;

  const payPledge = async (pledgeId, paidAs) => {
    setPayChoice(null);
    setPaying(p => ({ ...p, [pledgeId]: true }));
    try {
      const res = await axios.post(`${PUBLIC_API}/portal/${token}/pay`, {
        pledge_id: pledgeId,
        ...(paidAs ? { paid_as: paidAs } : {}),
      });
      if (res.data?.pay_url) {
        window.location.href = res.data.pay_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.err_pay_link"));
    } finally {
      setPaying(p => ({ ...p, [pledgeId]: false }));
    }
  };

  // Si le donateur a une société, on lui demande au clic « moi-même » ou « ma
  // société » (→ détermine le reçu Cerfa). Sinon, paiement direct en particulier.
  const hasCompany = !!(displayDonor && displayDonor.company_name);
  const onPayClick = (pledgeId) => {
    if (hasCompany) setPayChoice(pledgeId);
    else payPledge(pledgeId, "particulier");
  };

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF" }}>
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <span className="text-sm text-stone-500">{donor.first_name} {donor.last_name}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">{t("portal.personal_space")}</p>
        <h1 className="text-3xl font-light text-stone-900 mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {t("portal.hello", { name: displayDonor.first_name })}
        </h1>

        {/* Onglets synagogue (mode multi-org). On défilent horizontalement
            sur petit écran si beaucoup d'orgs. */}
        {isMultiOrg && (
          <div className="mb-6 bg-white rounded-2xl border border-stone-200 p-1.5 flex gap-1 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveOrgId(null)}
              className={`shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                activeOrgId === null ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
              }`}
            >
              {t("portal.all_orgs")}
            </button>
            {orgsList.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => setActiveOrgId(o.id)}
                className={`shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all inline-flex items-center gap-1.5 ${
                  activeOrgId === o.id ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                <Building2 className="h-3.5 w-3.5" />
                {o.name}
              </button>
            ))}
          </div>
        )}

        {/* Profile completion section — on passe le donateur de l'org active
            si on est sur un onglet précis, sinon l'identité primaire. */}
        <DonorProfileSection
          donor={displayDonor}
          token={token}
          onUpdate={() => window.location.reload()}
        />

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: t("portal.stat_total"), value: formatAmount(totalDisplayed), icon: TrendingUp },
            { label: t("portal.stat_pledges"), value: pledges.length, icon: HandCoins },
            { label: t("portal.stat_cerfas"), value: cerfas.length, icon: FileText },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-200">
                <Icon className="h-5 w-5 mb-3" style={{ color: "#B8962E" }} />
                <p className="text-2xl font-bold text-stone-900">{s.value}</p>
                <p className="text-xs text-stone-500 mt-0.5">{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Faire un NOUVEAU don — l'espace donateur ne servait qu'à régler
            l'existant : rien ne permettait de donner spontanément (demande
            Avi, 31/07/2026). On envoie vers la page de don publique de la
            synagogue (/donate/:orgId), qui gère montant libre, coordonnées
            et paiement. En multi-synagogues sur « Toutes » : un bouton par
            synagogue. */}
        {(activeOrg ? [activeOrg] : orgsList).length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 mb-6 px-6 py-5 flex flex-wrap items-center gap-3"
            data-testid="portal-new-donation">
            <div className="flex-1 min-w-[200px]">
              <h2 className="font-semibold text-stone-900">{t("portal.new_donation_title")}</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                {t("portal.new_donation_sub")}
              </p>
            </div>
            {(activeOrg ? [activeOrg] : orgsList).map(o => (
              <Link
                key={o.id}
                to={`/donate/${o.id}`}
                data-testid={`portal-donate-${o.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium hover:opacity-90 transition-opacity"
                style={{ background: "linear-gradient(135deg, #B8962E 0%, #C9A84C 100%)" }}
              >
                <HandCoins className="h-4 w-4" />
                {!activeOrg && isMultiOrg ? t("portal.donate_to", { name: o.name }) : t("portal.donate")}
              </Link>
            ))}
          </div>
        )}

        {/* Cerfa section */}
        <div className="bg-white rounded-2xl border border-stone-200 mb-6">
          <div className="px-6 py-4 border-b border-stone-100">
            <h2 className="font-semibold text-stone-900">{t("portal.cerfa_title")}</h2>
            <p className="text-xs text-stone-500 mt-0.5">{t("portal.cerfa_sub")}</p>
          </div>
          {cerfas.length === 0 ? (
            <div className="py-10 text-center text-stone-400 text-sm">{t("portal.cerfa_empty")}</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {cerfas.map(c => (
                <li key={c.id} className="flex items-center justify-between px-6 py-4 hover:bg-stone-50 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{c.motive}</p>
                    <p className="text-xs text-stone-500">{c.organization_name} · {c.issued_at ? new Date(c.issued_at).toLocaleDateString(dateLocale) : "—"}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-sm text-stone-900">{formatAmount(c.amount)}</span>
                    <button onClick={() => downloadCerfa(c)} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors">
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pledges section */}
        <div className="bg-white rounded-2xl border border-stone-200">
          <div className="px-6 py-4 border-b border-stone-100">
            <h2 className="font-semibold text-stone-900">{t("portal.pledges_title")}</h2>
          </div>
          {pledges.length === 0 ? (
            <div className="py-10 text-center text-stone-400 text-sm">{t("portal.pledges_empty")}</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {pledges.map(p => (
                <li key={p.id} className="flex items-center justify-between px-6 py-4 gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900">{p.motive}</p>
                    {p.description && <p className="text-xs text-stone-600">{p.description}</p>}
                    <p className="text-xs text-stone-500">
                      {p.organization_name}
                      {(p.donation_date || p.created_at) ? ` · ${new Date(p.donation_date || p.created_at).toLocaleDateString(dateLocale)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-stone-900">{formatAmount(p.amount)}</span>
                    <span className={`text-[11px] px-2.5 py-0.5 rounded-full border font-medium ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>
                      {STATUS_LABEL_KEYS[p.status] ? t(STATUS_LABEL_KEYS[p.status]) : p.status}
                    </span>
                    {/* Show "Payer" for every unpaid engagement, including
                        auto-confirmed seat reservations that land in status
                        "active" (not "pending"). Anything not fully paid
                        and not cancelled deserves the button. */}
                    {p.status !== "paid"
                      && p.status !== "cancelled"
                      && p.status !== "awaiting_approval"
                      && (p.amount_paid || 0) < (p.amount || 0) && (
                      <button onClick={() => onPayClick(p.id)} disabled={paying[p.id]}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-white transition-colors disabled:opacity-50"
                        style={{ background: "#0D1B33" }}>
                        {paying[p.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        {t("portal.pay_button")}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Choix au paiement : moi-même ou ma société (si une société est enregistrée) */}
      {payChoice && (
        <div
          className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4"
          onClick={() => setPayChoice(null)}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-900 mb-1">{t("portal.pay_as_title")}</h3>
            <p className="text-sm text-stone-500 mb-4">{t("portal.pay_as_sub")}</p>
            <div className="space-y-2">
              <button
                onClick={() => payPledge(payChoice, "particulier")}
                className="w-full text-left px-4 py-3 rounded-xl border border-stone-200 hover:border-stone-900 transition-colors"
              >
                <p className="text-sm font-medium text-stone-900">{t("portal.pay_as_self")}</p>
                <p className="text-xs text-stone-500">
                  {t("portal.pay_as_self_sub", { name: [displayDonor?.first_name, displayDonor?.last_name].filter(Boolean).join(" ") })}
                </p>
              </button>
              <button
                onClick={() => payPledge(payChoice, "entreprise")}
                className="w-full text-left px-4 py-3 rounded-xl border border-stone-200 hover:border-stone-900 transition-colors"
              >
                <p className="text-sm font-medium text-stone-900">{t("portal.pay_as_company")}</p>
                <p className="text-xs text-stone-500">
                  {t("portal.pay_as_company_sub", { name: displayDonor?.company_name || "" })}
                </p>
              </button>
            </div>
            <button
              onClick={() => setPayChoice(null)}
              className="mt-3 w-full text-sm text-stone-500 hover:text-stone-800 py-2"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Router component ──────────────────────────────────────────────────────
export default function DonorPortalPage() {
  const { token } = useParams();
  if (token) return <DonorDashboard token={token} />;
  return <RequestPortalPage />;
}
